import type { BookLanguage, StandardPageFormat } from '@kitup/book-schema';
import { defaultBookLanguage } from '@kitup/brand';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState, type FormEvent, type JSX } from 'react';
import type { BookPlanId } from '@kitup/book-schema';
import { chosenPlan, forgetChosenPlan } from './chosen-plan.js';
import { Link, useNavigate } from 'react-router';
import { booksApi } from '../api/books.js';
import { publicationApi } from '../api/publication.js';
import { useBrand } from '../brand/context.js';
import { userErrorMessage } from '../api/errors.js';
import { useI18n } from '../i18n/context.js';
import { Select } from '../components/Select.js';
import { OnboardingGuide } from '../books/OnboardingGuide.js';

export function BooksPage(): JSX.Element {
  const { t, locale, formatDate } = useI18n();
  const brand = useBrand();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const books = useQuery({ queryKey: ['books'], queryFn: booksApi.list });
  const [creating, setCreating] = useState(false);
  const [title, setTitle] = useState('');
  const [language, setLanguage] = useState<BookLanguage>(() => defaultBookLanguage(brand, locale));
  const [printSize, setPrintSize] = useState<StandardPageFormat>(brand.printSizes[0].format);
  const [message, setMessage] = useState<string | null>(null);

  const startCreating = (): void => {
    setLanguage(defaultBookLanguage(brand, locale));
    setPrintSize(brand.printSizes[0].format);
    setCreating(true);
  };

  const refresh = async (): Promise<void> => {
    await queryClient.invalidateQueries({ queryKey: ['books'] });
  };
  /*
   * A plan chosen on the pricing page ("Choose Living Book") is set on the next new book,
   * instead of the builder silently starting on its default. It arrives as `?plan=`, or was
   * kept by the sign-up page for a visitor who had to create an account first.
   */
  const plan: BookPlanId | undefined = chosenPlan();
  const create = useMutation({
    mutationFn: async (input: Parameters<typeof booksApi.create>[0]) => {
      const created = await booksApi.create(input);
      if (!plan || plan === created.plan) return created;
      // The book exists either way: a failed plan change must not read as a failed
      // creation, or "Add" again makes a second book. The plan can be picked in Basics.
      return booksApi.update(created.id, { plan }).catch(() => created);
    },
    onSuccess: refresh,
  });
  const archive = useMutation({ mutationFn: booksApi.archive, onSuccess: refresh });
  const duplicate = useMutation({
    mutationFn: (id: string) => booksApi.duplicate(id),
    onSuccess: refresh,
  });

  const submit = (event: FormEvent): void => {
    event.preventDefault();
    if (!title.trim()) return;
    setMessage(null);
    create.mutate(
      { title: title.trim(), language, printSize },
      {
        /*
         * Созданная книга сразу открывается в конструкторе: новичок должен
         * попасть на первый шаг сам, без подсказок из документации (E15-T2).
         */
        onSuccess: (created) => {
          forgetChosenPlan();
          setTitle('');
          setLanguage(defaultBookLanguage(brand, locale));
          setCreating(false);
          void navigate(`/app/books/${created.id}`);
        },
        onError: (error) => setMessage(userErrorMessage(error, t)),
      },
    );
  };

  if (books.isPending) return <p>{t.common.loading}</p>;
  if (books.isError) return <p role="alert">{t.common.unexpectedError}</p>;

  return (
    <section className="mx-auto max-w-7xl">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <h1 className="text-2xl font-semibold">{t.books.title}</h1>
        {/* On the empty page the guide below carries the one start button. */}
        {books.data.items.length > 0 && !creating ? (
          <button type="button" className="kitup-primary-button" onClick={startCreating}>
            {t.books.add}
          </button>
        ) : null}
      </div>

      {creating ? (
        <form onSubmit={submit} className="kitup-card mt-6 grid gap-4 p-5">
          <div className="grid gap-1.5">
            <label htmlFor="book-library-title" className="text-sm font-medium">
              {t.books.bookTitle}
            </label>
            <input
              id="book-library-title"
              autoFocus
              value={title}
              onChange={(event) => setTitle(event.target.value)}
              className="kitup-input"
              maxLength={200}
              required
              aria-describedby="book-library-title-hint"
            />
            <span id="book-library-title-hint" className="text-xs text-[var(--kitup-color-muted)]">
              {t.books.bookTitleHint}
            </span>
          </div>
          <div className="grid gap-1.5">
            <label htmlFor="book-language" className="text-sm font-medium">
              {t.books.bookLanguage}
            </label>
            <Select<BookLanguage>
              id="book-language"
              aria-describedby="book-language-hint"
              value={language}
              options={brand.bookLanguages.map((code) => ({
                value: code,
                label: t.books.languages[code],
              }))}
              onChange={setLanguage}
            />
            <span
              id="book-language-hint"
              className="text-xs font-normal text-[var(--kitup-color-muted)]"
            >
              {t.books.bookLanguageHint}
            </span>
          </div>
          {/* A brand with one size has nothing to ask. */}
          {brand.printSizes.length > 1 ? (
            <div className="grid gap-1.5">
              <label htmlFor="book-print-size" className="text-sm font-medium">
                {t.books.printSize}
              </label>
              <Select<StandardPageFormat>
                id="book-print-size"
                aria-describedby="book-print-size-hint"
                value={printSize}
                options={brand.printSizes.map((size) => ({
                  value: size.format,
                  label: size.names[locale] ?? size.names[brand.defaultLocale] ?? size.format,
                }))}
                onChange={setPrintSize}
              />
              <span
                id="book-print-size-hint"
                className="text-xs font-normal text-[var(--kitup-color-muted)]"
              >
                {t.books.printSizeHint}
              </span>
            </div>
          ) : null}
          {message ? (
            <p role="alert" className="text-sm text-red-700">
              {message}
            </p>
          ) : null}
          <div className="flex gap-3">
            <button className="kitup-primary-button" disabled={create.isPending}>
              {create.isPending ? t.common.loading : t.books.create}
            </button>
            <button
              type="button"
              className="kitup-secondary-button"
              onClick={() => setCreating(false)}
            >
              {t.common.cancel}
            </button>
          </div>
        </form>
      ) : null}

      {books.data.items.length === 0 ? (
        creating ? null : (
          <OnboardingGuide onStart={startCreating} />
        )
      ) : (
        <div className="mt-8 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {books.data.items.map((book) => (
            <article key={book.id} className="kitup-card overflow-hidden">
              {/*
               * The heading below stays the working title: the cover already shows the cover
               * title, and a copy inherits the original's cover title while only its working
               * title gets "(copy)", so the working title is what tells two cards apart.
               */}
              <BookCover
                url={book.coverUrl}
                title={book.title}
                coverTitle={book.coverTitle}
                onExpired={refresh}
              />
              <div className="p-4">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <h2 className="font-semibold">{book.title}</h2>
                    <p className="mt-1 text-xs text-[var(--kitup-color-muted)]">
                      {t.monetization.plans[book.plan].name} · {t.books.languages[book.language]} ·{' '}
                      {t.books.edited(formatDate(book.updatedAt))}
                    </p>
                  </div>
                  <span
                    className="kitup-status-pill whitespace-nowrap"
                    title={t.books.spreadsDrawn(book.sceneSummary.generated, book.sceneCount)}
                  >
                    {book.publishedAt
                      ? t.books.published
                      : t.books.spreadsDrawn(book.sceneSummary.generated, book.sceneCount)}
                  </span>
                </div>
                <div className="mt-4 flex flex-wrap gap-2 text-sm">
                  <Link className="kitup-primary-button" to={`/app/books/${book.id}`}>
                    {t.books.open}
                  </Link>
                  {book.publishedAt ? <CopyFamilyLink bookId={book.id} /> : null}
                  <button
                    type="button"
                    className="kitup-secondary-button"
                    onClick={() => duplicate.mutate(book.id)}
                  >
                    {t.books.duplicate}
                  </button>
                  <button
                    type="button"
                    className="kitup-quiet-button"
                    onClick={() => archive.mutate(book.id)}
                  >
                    {t.books.archive}
                  </button>
                </div>
              </div>
            </article>
          ))}
        </div>
      )}
    </section>
  );
}

/**
 * The family link from the list: before, it lived only on the builder's last step. The link
 * is fetched when the button is pressed, not for every published book on page load.
 */
function CopyFamilyLink({ bookId }: { readonly bookId: string }): JSX.Element | null {
  const { t } = useI18n();
  const queryClient = useQueryClient();
  const [state, setState] = useState<'idle' | 'copied' | 'failed'>('idle');
  useEffect(() => {
    if (state === 'idle') return;
    const timer = setTimeout(() => setState('idle'), 3_000);
    return () => clearTimeout(timer);
  }, [state]);
  if (!navigator.clipboard) return null;
  const copy = async (): Promise<void> => {
    try {
      // The Export step's own query and cache: a link fetched there is reused here.
      const publication = await queryClient.fetchQuery({
        queryKey: ['book-publication', bookId],
        queryFn: () => publicationApi.get(bookId),
      });
      if (!publication.published || !publication.url) throw new Error('not published');
      await navigator.clipboard.writeText(publication.url);
      setState('copied');
    } catch {
      setState('failed');
    }
  };
  return (
    <button type="button" className="kitup-secondary-button" onClick={() => void copy()}>
      {state === 'copied'
        ? t.publish.copied
        : state === 'failed'
          ? t.publish.copyFailed
          : t.publish.copy}
    </button>
  );
}

/**
 * The drawn cover, or the letter placeholder when there is none — or when the cover fails to
 * load. A signed URL expires (an hour, or less if it came from the URL cache), and a card
 * that outlives it showed a grey box with a broken-image icon: it falls back to the letter
 * and asks for the list again, which signs a fresh URL.
 */
function BookCover({
  url,
  title,
  coverTitle,
  onExpired,
}: {
  readonly url: string | null;
  readonly title: string;
  readonly coverTitle: string | null;
  onExpired(): Promise<void>;
}): JSX.Element {
  const [failedUrl, setFailedUrl] = useState<string | null>(null);
  if (!url || failedUrl === url) {
    return (
      <div className="kitup-book-cover-placeholder">
        <span>{title.slice(0, 1).toUpperCase()}</span>
      </div>
    );
  }
  const failed = (): void => {
    setFailedUrl(url);
    void onExpired();
  };
  return (
    <div className="kitup-book-cover">
      <img src={url} alt="" loading="lazy" onError={failed} />
      <img src={url} alt={coverTitle ?? ''} loading="lazy" onError={failed} />
    </div>
  );
}
